<template>
  <div class="mb-3 survey-form-questions">
    <label class="form-label">{{ prompt }}</label>
    <div>
      <Sortable
        :list="questions"
        item-key="uid"
        class="list-group"
        :options="{ draggable: '.list-group-item', handle: '.sort-handle', animation: 150 }"
        @end="storeQuestionData"
      >
        <template #item="{element: question, index: questionIdx}">
          <div :class="{ 'list-group-item': true, 'list-group-item-danger': question.rm, 'survey-section-item': questionIsSection(question) }">
            <div class="row gx-2">
              <div class="col-auto">
                <span
                  class="glyphicons sortable sort-handle"
                  title="Drag to reorder items"
                />
              </div>
              <div class="col"><span class="question-name">{{ getQuestionNumber(questionIdx) }} {{ questionName(question) }}</span><em v-if="question.rm">(will be deleted when saved)</em></div>
              <div class="col-auto">
                <button
                  type="button"
                  class="btn btn-sm btn-light"
                  @click.prevent="editQuestion(question, null)"
                >
                  <span
                    class="glyphicons edit"
                    title="Edit question"
                  />
                </button>
              </div>
            </div>
            <div
              v-if="question.allowSubQuestions"
              class="sub-questions"
            >
              <div v-if="hasSubQuestions(question)">
                <Sortable
                  :list="question.subQuestions"
                  item-key="uid"
                  :options="{ draggable: '.list-group-item', handle: '.sort-handle', animation: 150 }"
                  @end="storeSubQuestionData(question, $event)"
                >
                  <template #item="{element: subQuestion, index: subQuestionIdx}">
                    <div
                      :key="subQuestion.uid ?? undefined"
                      :class="{ 'list-group-item': true, 'list-group-item-danger': subQuestion.rm, 'bg-white': !subQuestion.rm }"
                    >
                      <div class="row gx-2">
                        <div class="col-auto">
                          <span
                            class="glyphicons sortable sort-handle"
                            title="Drag to reorder items"
                          />
                        </div>
                        <div class="col"><span class="question-name">{{ getQuestionNumber(questionIdx) }}.{{ subQuestionIdx + 1 }}. {{ questionName(subQuestion) }}</span><em v-if="subQuestion.rm">(will be deleted when saved)</em></div>
                        <div class="col-auto">
                          <button
                            type="button"
                            class="btn btn-sm btn-light"
                            @click.prevent="editQuestion(subQuestion, question)"
                          >
                            <span
                              class="glyphicons edit"
                              title="Edit question"
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </template>
                </Sortable>
              </div>
              <div
                v-else
                class="alert alert-info"
              >
                Please add trainer assessment questions.
              </div>
              <button
                type="button"
                class="btn btn-sm btn-success"
                @click.prevent="addSubQuestion(question)"
              >
                Add assessment question
              </button>
            </div>
          </div>
        </template>
      </Sortable>
    </div>
    <div
      v-if="hasNoQuestions"
      class="alert alert-info"
    >
      {{ emptyMessage }}
    </div>
    <button
      type="button"
      class="btn btn-sm btn-success"
      @click.prevent="newQuestion(null)"
    >
      Add new question
    </button>
    <button
      v-if="allowSections"
      class="btn btn-sm btn-success"
      @click.prevent="newSection"
    >
      Add new section
    </button>
    <teleport
      to="#vue-portal-target"
      append
    >
      <survey-question-modal
        ref="questionModal"
        :modal-title="modalProps.modalTitle"
        :question-groups="modalProps.questionGroups"
        @success="updateQuestion"
      />
      <survey-section-modal
        ref="sectionModal"
        @success="updateQuestion"
      />
    </teleport>
  </div>
</template>
<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef } from 'vue'
import {type SortableEvent} from 'sortablejs'
import {Sortable} from "sortablejs-vue3"

import SurveyQuestionModal from './survey-question-modal.vue'
import SurveySectionModal from './survey-section-modal.vue'
import { type Question, QuestionType, SurveySectionType } from './survey-types'
import {sortSortableList} from "@/js/js-utils";

const props = defineProps({
  prompt: { default: 'Questions', type: String },
  questionDataFieldRef: { default: null, type: String },
  allowSections: { default: false, type: Boolean }
})

const sectionNumbers = [
  '0', 'Ⅰ', 'Ⅱ', 'Ⅲ', 'Ⅳ', 'Ⅴ', 'Ⅵ', 'Ⅶ', 'Ⅷ', 'Ⅸ', 'Ⅹ', 'Ⅺ', 'Ⅻ'
]

const questionModal = useTemplateRef<InstanceType<typeof SurveyQuestionModal>>('questionModal')
const sectionModal = useTemplateRef<InstanceType<typeof SurveySectionModal>>('sectionModal')
const questions = ref<Question[]>([])
const modalProps = ref({
  modalTitle: '',
  questionGroups: [] as string[]
})
const questionDataFieldEl = ref<HTMLInputElement | null>(null)

const hasNoQuestions = computed((): boolean => {
  return questions.value.length == 0
})

const emptyMessage = computed((): string => {
  return 'Survey has no questions yet'
})

const questionGroups = computed((): string[] => {
  const groups = [] as string[]
  for (const q of questions.value) {
    if (typeof q.groupName === 'string' && q.groupName.length > 0) {
      groups.push(q.groupName)
    }
    if (Array.isArray(q.subQuestions)) {
      for (const sq of q.subQuestions) {
        if (typeof sq.groupName === 'string' && sq.groupName.length > 0) {
          groups.push(sq.groupName)
        }
      }
    }
  }

  return groups.filter(function(item, i, ar) { return ar.indexOf(item) === i })
})

const questionNumbering = computed((): string[] => {
  const result: string[] = []
  let curSectionNr = 0
  let curQuestionNr = 0

  for (const q of questions.value) {
    if (q.typeId == QuestionType.surveySection) {
      curSectionNr += 1
      curQuestionNr = 0 // reset question nr
      if (sectionNumbers.length > curSectionNr) {
        result.push(`${sectionNumbers[curSectionNr]} -`)
      } else {
        result.push(`${curSectionNr} -`)
      }
    } else {
      curQuestionNr += 1
      result.push(`${curQuestionNr}.`)
    }
  }

  return result
})

onMounted(() => {
  const inputFieldEl = document.querySelector(`[ref="${props.questionDataFieldRef}"]`)
  if (inputFieldEl instanceof HTMLInputElement) {
    questionDataFieldEl.value = inputFieldEl
    try {
      const initialQuestions = JSON.parse(questionDataFieldEl.value.value) as unknown
      if (Array.isArray(initialQuestions) && initialQuestions.length > 0) {
        for (const question of initialQuestions) {
          questions.value.push(question)
        }
      }
    } catch {
      // Ignore invalid stored question data and start with an empty list.
    }
  }
})

const questionName = (question: Question): string => {
  if (typeof question.nameFr === 'string' && question.nameFr.length > 0) {
    return `${ question.name } / ${ question.nameFr }`
  }
  return question.name || ''
}

const newQuestion = (parentQuestion: Question | null): void => {
  modalProps.value = {
    modalTitle: 'Add question',
    questionGroups: questionGroups.value,
  }
  if (parentQuestion) {
    modalProps.value.modalTitle = `Add question (${questionName(parentQuestion)})`
  }
  const newQuestionData: Question = {
    uid: null,
    id: null,
    typeId: null,
    rm: false,
    name: '',
    nameFr: '',
    description: '',
    descriptionFr: '',
    groupName: null,
    isMandatory: false,
    allowNotes: false,
    subQuestions: [],
    allowSubQuestions: false,
    options: {
      displayInOneLine: false,
      ratingMin: null,
      ratingMax: null,
      ratingNotApplicable: false,
      ratingDefinitions: [],
      ratingTableChoiceWidth: null,
      choices: [],
      sectionTypeId: SurveySectionType.general,
      showRatingDefinitions: false,
      notesMandatory: false,
      notesCustomLabel: false,
      notesLabel: '',
      notesLabelFr: '',
      notesMinLength: null,
      uploadCountMax: 1,
      textMinLength: null,
      choicesInSingleColumn: false
    }
  }
  nextTick(() => {
    questionModal.value?.showModal(newQuestionData, parentQuestion, false)
  })
}

const newSection = (): void => {
  const newSectionData: Question = {
    uid: null,
    id: null,
    typeId: QuestionType.surveySection,
    rm: false,
    name: '',
    nameFr: '',
    description: '',
    descriptionFr: '',
    groupName: null,
    isMandatory: false,
    allowNotes: false,
    subQuestions: [],
    allowSubQuestions: false,
    options: {
      displayInOneLine: false,
      ratingMin: null,
      ratingMax: null,
      ratingNotApplicable: false,
      ratingDefinitions: [],
      ratingTableChoiceWidth: null,
      showRatingDefinitions: false,
      choices: [],
      sectionTypeId: SurveySectionType.general,
      notesMandatory: false,
      notesCustomLabel: false,
      notesLabel: '',
      notesLabelFr: '',
      notesMinLength: null,
      uploadCountMax: null,
      textMinLength: null,
      choicesInSingleColumn: false
    }
  }

  sectionModal.value?.showModal(newSectionData, false)
}

const editQuestion = (question: Question, parentQuestion: Question | null): void => {
  if (question.typeId == QuestionType.surveySection) {
    sectionModal.value?.showModal(question, true)
  } else {
    modalProps.value = {
      modalTitle: 'Edit question',
      questionGroups: questionGroups.value,
    }
    nextTick(() => {
      questionModal.value?.showModal(question, parentQuestion, true)
    })
  }
}

const storeQuestionData = (sortableEvent?: SortableEvent): void => {
  if (!questionDataFieldEl.value) {
    return
  }

  sortSortableList(questions.value, sortableEvent)
  questionDataFieldEl.value.value = JSON.stringify(questions.value)
}

const updateQuestion = (question: Question, parentQuestion: Question | null): void => {
  if (question) {
    if (question.uid === null) {
      question.uid = `q-${new Date().getTime()}`
      if (parentQuestion) {
        parentQuestion.subQuestions.push(question)
      } else {
        questions.value.push(question)
      }
    } else {
      let updatedQuestions = questions.value
      if (parentQuestion) {
        updatedQuestions = parentQuestion.subQuestions
      }
      for (let i = 0; i < updatedQuestions.length; i++) {
        if (updatedQuestions[i].uid === question.uid) {
          if (question.rm && question.id == null) {
            // removing question that hasn't been saved
            updatedQuestions.splice(i, 1)
          } else {
            updatedQuestions.splice(i, 1, question)
          }
          break
        }
      }
    }
  }
  questionModal.value?.hideModal()
  sectionModal.value?.hideModal()

  storeQuestionData()
}

const hasSubQuestions = (question: Question): boolean => {
  return Array.isArray(question.subQuestions) && question.subQuestions.length > 0
}

const addSubQuestion = (parentQuestion: Question): void => {
  newQuestion(parentQuestion)
}

const storeSubQuestionData = (question: Question, event: SortableEvent): void => {
  sortSortableList(question.subQuestions, event)
  storeQuestionData()
}

const getQuestionNumber = (questionIdx: number): string => {
  if (questionNumbering.value.length > questionIdx) {
    return questionNumbering.value[questionIdx]
  }
  return '--'
}

const questionIsSection = (question: Question): boolean => {
  return question.typeId == QuestionType.surveySection
}
</script>
